import { Eye } from 'lucide-react'
import * as React from 'react'

import { ResolvedUserImage } from '@/components/common/resolved-media-image'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

import { formatViewerHoverText, MessageViewer } from '@/utils/chat-message-viewers'

interface MessageViewersHoverProps {
  viewers: MessageViewer[]
  children: React.ReactElement
  side?: 'top' | 'bottom' | 'left' | 'right'
}

export const MessageViewersHover: React.FC<MessageViewersHoverProps> = ({
  viewers,
  children,
  side = 'top',
}) => {
  if (viewers.length === 0) {
    return children
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-xs">
          {formatViewerHoverText(viewers)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface LatestMessageViewerAvatarsProps {
  viewers: MessageViewer[]
  align?: 'start' | 'end'
  onClick?: () => void
}

export const LatestMessageViewerAvatars: React.FC<LatestMessageViewerAvatarsProps> = ({
  viewers,
  align = 'end',
  onClick,
}) => {
  if (viewers.length === 0) return null

  const maxDisplayed = 3
  const displayedViewers = viewers.slice(0, maxDisplayed)
  const remainingCount = viewers.length - maxDisplayed

  return (
    <div
      className={`mt-1 flex items-center ${align === 'end' ? 'justify-end' : 'justify-start'} ${
        onClick ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center -space-x-1.5">
        {displayedViewers.map((viewer, index) => (
          <div
            key={viewer.id}
            className="relative h-4 w-4 overflow-hidden rounded-full border border-white bg-gray-100"
            style={{ zIndex: maxDisplayed - index }}
            title={`${viewer.user.firstName} ${viewer.user.lastName}`}
          >
            <ResolvedUserImage
              avatar={viewer.user.avatar}
              alt={`${viewer.user.firstName} ${viewer.user.lastName}`}
              size={16}
              className="h-4 w-4"
              placeholderClassName="h-4 w-4"
            />
          </div>
        ))}
        {remainingCount > 0 && (
          <div
            className="relative z-0 flex h-4 w-4 items-center justify-center rounded-full border border-white bg-blue-500 text-[8px] font-medium text-white"
            title={`+${remainingCount} người khác`}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    </div>
  )
}

interface ShowAllViewersButtonProps {
  viewers: MessageViewer[]
  onShowAll: () => void
  className?: string
}

export const ShowAllViewersButton: React.FC<ShowAllViewersButtonProps> = ({
  viewers,
  onShowAll,
  className,
}) => {
  if (viewers.length === 0) return null

  return (
    <button
      type="button"
      onClick={onShowAll}
      className={`rounded-full p-1.5 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 ${className ?? ''}`}
      title="Xem người đã xem"
      aria-label="Xem người đã xem"
    >
      <Eye className="h-3 w-3 text-gray-500 transition-colors duration-200 hover:text-blue-600" />
    </button>
  )
}

interface MessageViewersDialogProps {
  viewers: MessageViewer[]
  onClose: () => void
  onUserClick?: (viewer: MessageViewer) => void
}

export const MessageViewersDialog: React.FC<MessageViewersDialogProps> = ({
  viewers,
  onClose,
  onUserClick,
}) => (
  <div className="fixed inset-0 z-[10001] flex items-center justify-center">
    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
    <div className="relative mx-4 flex max-h-[80vh] w-full max-w-md flex-col rounded-lg bg-white shadow-lg">
      <div className="flex items-center justify-between border-b p-4">
        <h3 className="text-lg font-semibold">Người đã xem ({viewers.length})</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Đóng">
          ✕
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {viewers.map(viewer => (
            <div key={viewer.id} className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <ResolvedUserImage
                  avatar={viewer.user.avatar}
                  alt={`${viewer.user.firstName} ${viewer.user.lastName}`}
                  size={40}
                  className="h-10 w-10"
                  placeholderClassName="h-10 w-10"
                />
              </div>
              <div className="flex-1">
                {onUserClick ? (
                  <button
                    onClick={() => onUserClick(viewer)}
                    className="cursor-pointer text-left font-medium transition-colors hover:text-blue-600 hover:underline"
                  >
                    {viewer.user.firstName} {viewer.user.lastName}
                  </button>
                ) : (
                  <p className="font-medium">
                    {viewer.user.firstName} {viewer.user.lastName}
                  </p>
                )}
                <p className="text-sm text-gray-500">
                  Đã xem lúc {viewer.readAt ? new Date(viewer.readAt).toLocaleString() : 'Không rõ'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end border-t p-4">
        <Button onClick={onClose} variant="outline">
          Đóng
        </Button>
      </div>
    </div>
  </div>
)
